import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  message
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  EyeOutlined,
  PlusOutlined,
  QrcodeOutlined,
  ReloadOutlined,
  StopOutlined
} from '@ant-design/icons';
import { QRCode } from 'react-qrcode-logo';
import { invitationCodeAPI, schoolAdminAPI, schoolAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { resolveDefaultSchoolId, shouldLockSchoolSelection, normalizeScopeId } from '../../utils/schoolScope';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const statusMap = {
  active: { color: 'success', text: '有效' },
  expired: { color: 'default', text: '已过期' },
  disabled: { color: 'error', text: '已禁用' }
};

const typeMap = {
  teacher: { color: 'green', text: '教师入校' },
  school_admin: { color: 'blue', text: '学校管理员' }
};

function InvitationCodes() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [codes, setCodes] = useState([]);
  const [filters, setFilters] = useState({
    status: '',
    invitation_type: ''
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [qrCodeModalVisible, setQrCodeModalVisible] = useState(false);
  const [currentCode, setCurrentCode] = useState(null);
  const [joinPasswordLoading, setJoinPasswordLoading] = useState(false);
  const [joinPasswordState, setJoinPasswordState] = useState(null);
  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState(null);

  const [createForm] = Form.useForm();
  const [joinPasswordForm] = Form.useForm();

  const selectedInvitationType = Form.useWatch('invitation_type', createForm) || 'teacher';
  const customCode = Form.useWatch('custom_code', createForm) || '';
  const canManageAdminCodes = user?.role === 'super_admin';
  const canDeleteCodes = user?.role === 'super_admin';

  useEffect(() => {
    fetchCodes();
  }, [pagination.current, pagination.pageSize, filters.status, filters.invitation_type]);

  useEffect(() => {
    if (user?.role !== 'school_admin') return;
    schoolAPI.getAll().then((response) => {
      const availableSchools = response.data?.data || [];
      setSchools(availableSchools);
      setSchoolId(resolveDefaultSchoolId(user, availableSchools));
    }).catch((error) => {
      message.error(error.response?.data?.message || '获取授权学校失败');
    });
  }, [user]);

  const fetchJoinPasswordState = async () => {
    if (!schoolId) return;
    try {
      setJoinPasswordLoading(true);
      const response = await schoolAdminAPI.getJoinPassword({ schoolId });
      if (response.data.success) setJoinPasswordState(response.data.data);
    } catch (error) {
      message.error(error.response?.data?.message || '获取学校加入密码状态失败');
    } finally {
      setJoinPasswordLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === 'school_admin' && schoolId) fetchJoinPasswordState();
  }, [user?.role, schoolId]);

  const saveJoinPassword = async ({ password }) => {
    if (!schoolId) {
      message.warning('请先选择学校');
      return;
    }
    try {
      setJoinPasswordLoading(true);
      await schoolAdminAPI.setJoinPassword({ schoolId, password });
      joinPasswordForm.resetFields();
      message.success('学校加入密码已生效');
      await fetchJoinPasswordState();
    } catch (error) {
      message.error(error.response?.data?.message || '设置加入密码失败');
    } finally {
      setJoinPasswordLoading(false);
    }
  };

  const stats = useMemo(() => ({
    active: codes.filter(item => item.status === 'active').length,
    expired: codes.filter(item => item.status === 'expired').length,
    disabled: codes.filter(item => item.status === 'disabled').length
  }), [codes]);

  const fetchCodes = async () => {
    try {
      setLoading(true);
      const response = await invitationCodeAPI.getAll({
        page: pagination.current,
        limit: pagination.pageSize,
        ...filters
      });

      if (!response.data.success) {
        message.error(response.data.message || '获取邀请码列表失败');
        return;
      }

      setCodes(response.data.data.codes || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.data.pagination?.total || 0
      }));
    } catch (error) {
      console.error('获取邀请码列表失败:', error);
      message.error('获取邀请码列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (values) => {
    const { count = 1, ...restValues } = values;
    const normalizedCustomCode = String(restValues.custom_code || '').trim();
    const generateCount = normalizedCustomCode ? 1 : count;
    const createdCodes = [];

    try {
      setLoading(true);

      for (let index = 0; index < generateCount; index += 1) {
        const response = await invitationCodeAPI.create({
          ...restValues,
          custom_code: normalizedCustomCode || undefined
        });

        if (!response.data.success) {
          message.error(response.data.message || `第 ${index + 1} 个邀请码生成失败`);
          break;
        }

        createdCodes.push(response.data.data);
      }

      if (!createdCodes.length) {
        return;
      }

      message.success(`成功生成 ${createdCodes.length} 个邀请码`);
      setCreateModalVisible(false);
      createForm.resetFields();
      await fetchCodes();

      Modal.success({
        title: '邀请码生成成功',
        width: 640,
        content: (
          <div>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="学校邀请码可直接复用到教师注册和入校申请流程"
              description="如果这里填写的是数字学校邀请码，老师扫码或输入后会自动带出目标学校，并提交到该学校的待审核入校申请。"
            />
            <div style={{ maxHeight: 360, overflowY: 'auto' }}>
              {createdCodes.map((item, index) => (
                <Card
                  key={item.id}
                  size="small"
                  style={{ marginBottom: 12 }}
                  bodyStyle={{ padding: 12 }}
                >
                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <Text strong>邀请码 {index + 1}</Text>
                    <Text code copyable style={{ fontSize: 16 }}>
                      {item.code}
                    </Text>
                    <Text type="secondary">
                      类型：{typeMap[item.invitation_type]?.text || item.invitation_type}
                      {item.school_name ? ` ｜ 学校：${item.school_name}` : ''}
                    </Text>
                  </Space>
                </Card>
              ))}
            </div>
          </div>
        )
      });
    } catch (error) {
      console.error('创建邀请码失败:', error);
      message.error(error.response?.data?.message || '创建邀请码失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      message.success('邀请码已复制');
    } catch (error) {
      console.error('复制邀请码失败:', error);
      message.error('复制失败，请手动复制');
    }
  };

  const handleDisable = async (id) => {
    try {
      const response = await invitationCodeAPI.disable(id);
      if (!response.data.success) {
        message.error(response.data.message || '禁用邀请码失败');
        return;
      }
      message.success('邀请码已禁用');
      fetchCodes();
    } catch (error) {
      console.error('禁用邀请码失败:', error);
      message.error('禁用邀请码失败');
    }
  };

  const handleEnable = async (id) => {
    try {
      const response = await invitationCodeAPI.enable(id);
      if (!response.data.success) {
        message.error(response.data.message || '启用邀请码失败');
        return;
      }
      message.success('邀请码已启用');
      fetchCodes();
    } catch (error) {
      console.error('启用邀请码失败:', error);
      message.error(error.response?.data?.message || '启用邀请码失败');
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await invitationCodeAPI.delete(id);
      if (!response.data.success) {
        message.error(response.data.message || '删除邀请码失败');
        return;
      }
      message.success('邀请码已删除');
      fetchCodes();
    } catch (error) {
      console.error('删除邀请码失败:', error);
      message.error(error.response?.data?.message || '删除邀请码失败');
    }
  };

  const showDetail = async (id) => {
    try {
      const response = await invitationCodeAPI.getById(id);
      if (!response.data.success) {
        message.error(response.data.message || '获取邀请码详情失败');
        return;
      }
      setCurrentCode(response.data.data);
      setDetailModalVisible(true);
    } catch (error) {
      console.error('获取邀请码详情失败:', error);
      message.error('获取邀请码详情失败');
    }
  };

  const columns = [
    {
      title: '邀请码',
      dataIndex: 'code',
      key: 'code',
      width: 180,
      render: (value) => (
        <Space>
          <Text code strong style={{ fontSize: 15 }}>
            {value}
          </Text>
          <Tooltip title="复制邀请码">
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => handleCopy(value)}
            />
          </Tooltip>
        </Space>
      )
    },
    {
      title: '类型',
      dataIndex: 'invitation_type',
      key: 'invitation_type',
      width: 120,
      render: (value) => {
        const type = typeMap[value] || { color: 'default', text: value };
        return <Tag color={type.color}>{type.text}</Tag>;
      }
    },
    {
      title: '关联学校',
      dataIndex: 'school_name',
      key: 'school_name',
      width: 220,
      render: (value) => value || <Text type="secondary">未指定学校</Text>
    },
    {
      title: '使用情况',
      key: 'usage',
      width: 130,
      render: (_, record) => (
        <Text>
          {record.used_count} / {record.max_uses > 0 ? record.max_uses : '不限'}
        </Text>
      )
    },
    {
      title: '有效期',
      dataIndex: 'expires_at',
      key: 'expires_at',
      width: 180,
      render: (value) => {
        if (!value) {
          return <Text type="secondary">长期有效</Text>;
        }
        return new Date(value).toLocaleString();
      }
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (value) => {
        const status = statusMap[value] || { color: 'default', text: value };
        return <Tag color={status.color}>{status.text}</Tag>;
      }
    },
    {
      title: '创建人',
      dataIndex: 'created_by_name',
      key: 'created_by_name',
      width: 120,
      render: (value, record) => value || record.created_by_username || '-'
    },
    {
      title: '操作',
      key: 'actions',
      width: 220,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="查看详情">
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => showDetail(record.id)}
            />
          </Tooltip>
          <Tooltip title="查看二维码">
            <Button
              type="link"
              size="small"
              icon={<QrcodeOutlined />}
              onClick={() => {
                setCurrentCode(record);
                setQrCodeModalVisible(true);
              }}
            />
          </Tooltip>
          {record.status === 'active' && (
            <Popconfirm
              title="确认禁用这个邀请码吗？"
              onConfirm={() => handleDisable(record.id)}
              okText="确认"
              cancelText="取消"
            >
              <Tooltip title="禁用">
                <Button type="link" size="small" danger icon={<StopOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
          {record.status === 'disabled' && (
            <Tooltip title="启用">
              <Button
                type="link"
                size="small"
                icon={<CheckCircleOutlined />}
                onClick={() => handleEnable(record.id)}
              />
            </Tooltip>
          )}
          {canDeleteCodes && !record.used_count && (
            <Popconfirm
              title="确认删除这个邀请码吗？"
              onConfirm={() => handleDelete(record.id)}
              okText="确认"
              cancelText="取消"
            >
              <Button type="link" size="small" danger>
                删除
              </Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ marginBottom: 8 }}>
          <QrcodeOutlined style={{ marginRight: 8 }} />
          邀请码管理
        </Title>
        <Text type="secondary">
          复用现有邀请码管理来生成学校二维码。学校管理员可设置数字学校邀请码，供教师扫码或输入后提交入校申请。
        </Text>
      </div>

      {user?.role === 'school_admin' ? (
        <Card title="学校教师加入密码" loading={joinPasswordLoading} style={{ marginBottom: 24 }}>
          <Select
            value={schoolId}
            onChange={(value) => {
              setSchoolId(value);
              setJoinPasswordState(null);
            }}
            disabled={shouldLockSchoolSelection(user, schools)}
            style={{ width: 320, marginBottom: 16 }}
            placeholder="选择授权学校"
          >
            {schools.map(school => (
              <Option key={school.id} value={normalizeScopeId(school.id)}>{school.name}</Option>
            ))}
          </Select>
          <Alert
            type={joinPasswordState?.passwordConfigured ? 'success' : 'warning'}
            showIcon
            style={{ marginBottom: 18 }}
            message={joinPasswordState?.passwordConfigured ? '加入密码已设置' : '尚未设置加入密码'}
            description="学校已有管理员后，教师必须输入此密码才能直接加入。密码不会明文显示；忘记后请直接设置新密码。"
          />
          <Form form={joinPasswordForm} layout="inline" onFinish={saveJoinPassword}>
            <Form.Item
              name="password"
              rules={[
                { required: true, message: '请输入加入密码' },
                { pattern: /^[A-Za-z0-9]{4,20}$/, message: '请输入4至20位字母或数字' }
              ]}
            >
              <Input.Password style={{ width: 280 }} maxLength={20} placeholder="设置新的学校加入密码" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={joinPasswordLoading}>
                {joinPasswordState?.passwordConfigured ? '更换加入密码' : '设置加入密码'}
              </Button>
            </Form.Item>
          </Form>
        </Card>
      ) : null}

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
        message="学校加入密码与一次性邀请码相互独立"
        description="标准学校没有管理员时教师可直接加入；已有管理员时，教师向管理员获取学校加入密码，验证正确后直接加入，无需人工审核。"
      />

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} md={6}>
          <Card>
            <Statistic title="总数" value={pagination.total} prefix={<QrcodeOutlined />} />
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card>
            <Statistic
              title="当前页有效"
              value={stats.active}
              valueStyle={{ color: '#3f8600' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card>
            <Statistic
              title="当前页已过期"
              value={stats.expired}
              valueStyle={{ color: '#999' }}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card>
            <Statistic
              title="当前页已禁用"
              value={stats.disabled}
              valueStyle={{ color: '#cf1322' }}
              prefix={<StopOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title="邀请码列表"
        extra={(
          <Space wrap>
            <Select
              allowClear
              placeholder="筛选状态"
              style={{ width: 120 }}
              value={filters.status || undefined}
              onChange={(value) => {
                setPagination(prev => ({ ...prev, current: 1 }));
                setFilters(prev => ({ ...prev, status: value || '' }));
              }}
            >
              <Option value="active">有效</Option>
              <Option value="expired">已过期</Option>
              <Option value="disabled">已禁用</Option>
            </Select>
            <Select
              allowClear
              placeholder="筛选类型"
              style={{ width: 140 }}
              value={filters.invitation_type || undefined}
              onChange={(value) => {
                setPagination(prev => ({ ...prev, current: 1 }));
                setFilters(prev => ({ ...prev, invitation_type: value || '' }));
              }}
            >
              <Option value="teacher">教师入校</Option>
              <Option value="school_admin">学校管理员</Option>
            </Select>
            <Button icon={<ReloadOutlined />} onClick={fetchCodes}>
              刷新
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalVisible(true)}>
              生成邀请码
            </Button>
          </Space>
        )}
      >
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={codes}
          scroll={{ x: 1280 }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
            onChange: (page, pageSize) => {
              setPagination(prev => ({
                ...prev,
                current: page,
                pageSize
              }));
            }
          }}
        />
      </Card>

      <Modal
        title="生成邀请码"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          createForm.resetFields();
        }}
        footer={null}
        width={640}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="数字学校邀请码仅用于教师入校"
          description="如果填写学校邀请码，将只生成 1 个教师邀请码，并建议使用 4 到 12 位数字，方便教师扫码或手动输入后提交入校申请。"
        />
        <Form
          form={createForm}
          layout="vertical"
          initialValues={{
            invitation_type: 'teacher',
            count: 1,
            max_uses: 10,
            expiry_days: 30
          }}
          onFinish={handleCreate}
        >
          <Form.Item
            name="invitation_type"
            label="邀请码类型"
            rules={[{ required: true, message: '请选择邀请码类型' }]}
          >
            <Select>
              <Option value="teacher">教师入校</Option>
              {canManageAdminCodes ? (
                <Option value="school_admin">学校管理员</Option>
              ) : null}
            </Select>
          </Form.Item>

          <Form.Item
            name="custom_code"
            label="学校邀请码"
            extra="可选。填写后会使用这个数字码作为学校邀请码，老师扫码或输入后即可发起加入本校的审核申请。"
            rules={[
              {
                validator: (_, value) => {
                  const normalizedValue = String(value || '').trim();
                  if (!normalizedValue) {
                    return Promise.resolve();
                  }
                  if (selectedInvitationType !== 'teacher') {
                    return Promise.reject(new Error('学校邀请码仅支持教师类型'));
                  }
                  if (!/^\d{4,12}$/.test(normalizedValue)) {
                    return Promise.reject(new Error('学校邀请码必须是 4 到 12 位数字'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input
              maxLength={12}
              placeholder="例如：668899"
              disabled={selectedInvitationType !== 'teacher'}
            />
          </Form.Item>

          <Form.Item
            name="count"
            label="生成数量"
            rules={[{ required: true, message: '请输入生成数量' }]}
            extra={customCode ? '填写学校邀请码后会固定只生成 1 个。' : '一次最多建议生成 100 个邀请码。'}
          >
            <InputNumber min={1} max={100} style={{ width: '100%' }} disabled={Boolean(customCode)} />
          </Form.Item>

          <Form.Item
            name="max_uses"
            label="最大使用次数"
            rules={[{ required: true, message: '请输入最大使用次数' }]}
            extra="-1 表示不限次数"
          >
            <InputNumber min={-1} max={10000} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="expiry_days"
            label="有效期（天）"
            rules={[{ required: true, message: '请输入有效期' }]}
          >
            <InputNumber min={1} max={3650} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="notes" label="备注">
            <TextArea rows={3} placeholder="例如：2026 春季新教师入校邀请码" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                生成
              </Button>
              <Button onClick={() => setCreateModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="邀请码详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={(
          <Button onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>
        )}
        width={760}
      >
        {currentCode && (
          <>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="邀请码" span={2}>
                <Space>
                  <Text code strong style={{ fontSize: 16 }}>
                    {currentCode.code}
                  </Text>
                  <Button type="link" size="small" icon={<CopyOutlined />} onClick={() => handleCopy(currentCode.code)}>
                    复制
                  </Button>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="类型">
                <Tag color={typeMap[currentCode.invitation_type]?.color || 'default'}>
                  {typeMap[currentCode.invitation_type]?.text || currentCode.invitation_type}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="状态">
                <Tag color={statusMap[currentCode.status]?.color || 'default'}>
                  {statusMap[currentCode.status]?.text || currentCode.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="关联学校">
                {currentCode.school_name || '未指定学校'}
              </Descriptions.Item>
              <Descriptions.Item label="创建人">
                {currentCode.created_by_name || currentCode.created_by_username || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="使用次数">
                {currentCode.used_count} / {currentCode.max_uses > 0 ? currentCode.max_uses : '不限'}
              </Descriptions.Item>
              <Descriptions.Item label="剩余次数">
                {currentCode.max_uses > 0
                  ? Math.max(0, currentCode.max_uses - currentCode.used_count)
                  : '不限'}
              </Descriptions.Item>
              <Descriptions.Item label="有效期" span={2}>
                {currentCode.expires_at ? new Date(currentCode.expires_at).toLocaleString() : '长期有效'}
              </Descriptions.Item>
              <Descriptions.Item label="创建时间" span={2}>
                {currentCode.created_at ? new Date(currentCode.created_at).toLocaleString() : '-'}
              </Descriptions.Item>
              {currentCode.notes ? (
                <Descriptions.Item label="备注" span={2}>
                  {currentCode.notes}
                </Descriptions.Item>
              ) : null}
            </Descriptions>

            {Array.isArray(currentCode.usage_records) && currentCode.usage_records.length > 0 ? (
              <>
                <Divider>使用记录</Divider>
                <Table
                  rowKey="id"
                  size="small"
                  pagination={false}
                  dataSource={currentCode.usage_records}
                  columns={[
                    {
                      title: '使用人',
                      dataIndex: 'name',
                      key: 'name',
                      render: (value, record) => value || record.username || '-'
                    },
                    {
                      title: '邮箱',
                      dataIndex: 'email',
                      key: 'email',
                      render: (value) => value || '-'
                    },
                    {
                      title: '使用时间',
                      dataIndex: 'used_at',
                      key: 'used_at',
                      render: (value) => value ? new Date(value).toLocaleString() : '-'
                    }
                  ]}
                />
              </>
            ) : null}
          </>
        )}
      </Modal>

      <Modal
        title="邀请码二维码"
        open={qrCodeModalVisible}
        onCancel={() => setQrCodeModalVisible(false)}
        footer={(
          <Button onClick={() => setQrCodeModalVisible(false)}>
            关闭
          </Button>
        )}
        width={420}
      >
        {currentCode && (
          <div style={{ textAlign: 'center' }}>
            <QRCode value={currentCode.code} size={240} level="H" includeMargin />
            <div style={{ marginTop: 16 }}>
              <Text strong style={{ fontSize: 24 }}>
                {currentCode.code}
              </Text>
            </div>
            <div style={{ marginTop: 16 }}>
              <Button type="primary" icon={<CopyOutlined />} onClick={() => handleCopy(currentCode.code)}>
                复制邀请码
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default InvitationCodes;
